import { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import { KpiBand } from '../components/ui/Kpi';
import Pill, { PhaseChip, PillTone } from '../components/ui/Pill';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { ColumnChart } from '../components/ui/Charts';
import { LoadingPanel, ErrorNotice, SkeletonRows } from '../components/ui/States';
import { fmt, pct, dateLong, orDash, relative, utcStamp } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';

interface IntegrationHealth {
  status: 'healthy' | 'failed' | 'running' | 'planned';
  at: string | null;
  records: number | null;
  message: string | null;
}

interface DashboardData {
  kpis: {
    totalMembers: number;
    activeMembers: number;
    clusters: number;
    registrations30: number;
    registrationsDelta: number;
    attendance30: number;
    sessions30: number;
  };
  registrationsByMonth: { month: string; label: string; count: number; current: boolean }[];
  integrations: {
    ibmDirectory: IntegrationHealth;
    moodle: IntegrationHealth;
    attendance: IntegrationHealth;
    webhook: { status: string; at: string | null; formId: string | null };
    mentorship: IntegrationHealth;
  };
  latestRegistrations: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    region: string | null;
    careerStage: string | null;
    receivedAt: string;
  }[];
  onboardingByMonth: {
    month: string;
    label: string;
    created: number;
    adopted: number;
    partial: number;
    failed: number;
    running: number;
  }[];
  attention: {
    withoutIbmAccount: number;
    ibmLinkPending: number;
    inactiveOverYear: number;
    passwordUndelivered: number;
    ibmDeleted: number;
    emailChangeIncomplete: number;
    moodleUnmatched: number;
    formsToClassify?: number;
    formResponsesUnmatched?: number;
    formResponsesMissing?: number;
    missedRegistrations?: number;
    possibleDuplicates?: number;
    webinarsMissingAttendance?: number;
  };
}

type AttentionKind =
  'password-undelivered' | 'ibm-deleted' | 'email-change-incomplete' | 'moodle-unmatched' | 'missed-registration';

interface AttentionRow {
  userId?: string;
  at?: string | null;
  error?: string | null;
  member?: { id: string; firstName: string; lastName: string; email: string };
  moodle?: {
    moodleUserId: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    auth: string | null;
    suspended: boolean;
    lastAccessAt: string | null;
  };
}

const MOODLE_AUTH: Record<string, string> = {
  oauth2: 'IBM sign-in',
  email: 'self-registered',
  manual: 'local Moodle account',
};

const TONE: Record<string, PillTone> = {
  healthy: 'healthy',
  failed: 'failed',
  running: 'running',
  planned: 'planned',
};

const LABEL: Record<string, string> = {
  healthy: 'Healthy',
  failed: 'Failed',
  running: 'Running',
  planned: 'Planned',
};

function IntegrationRow({
  name,
  meta,
  health,
  pillLabel,
}: {
  name: string;
  meta: string;
  health: IntegrationHealth['status'];
  pillLabel?: string;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-4 px-5 py-[14px] border-b border-line last:border-b-0 ${
        health === 'failed' ? 'bg-warning-tint' : ''
      }`}
    >
      <div className="min-w-0">
        <div className="font-semibold text-table normal-case">{name}</div>
        <div className="text-small text-ink-2 mt-[2px] truncate">{meta}</div>
      </div>
      <Pill tone={TONE[health] ?? 'planned'}>{pillLabel ?? LABEL[health] ?? 'Planned'}</Pill>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const [reviewKind, setReviewKind] = useState<{ kind: AttentionKind; label: string } | null>(null);

  const review = useQuery<AttentionRow[]>(
    ['attention', reviewKind?.kind],
    async () => (await api.get(`/analytics/attention/${reviewKind?.kind}`)).data.data,
    { enabled: Boolean(reviewKind) },
  );

  const { data, isLoading, error } = useQuery<DashboardData>('dashboard', async () => {
    const res = await api.get('/analytics/dashboard');
    return res.data.data;
  });

  const openMember = (id: string) => {
    navigate(`/analytics/members/${id}`);
  };

  const k = data?.kpis;
  const attentionItems = data
    ? [
        {
          count: data.attention.missedRegistrations ?? 0,
          label: 'New registrations the website didn’t deliver',
          note: 'Found by the nightly pull, so no account or welcome email yet — provision from Member 360',
          phase: false,
          kind: 'missed-registration' as AttentionKind,
        },
        {
          count: data.attention.possibleDuplicates ?? 0,
          label: 'Possible duplicates',
          note: 'Members who may have registered twice under different emails — merge or mark not the same person',
          phase: false,
          href: '/members',
        },
        {
          count: data.attention.webinarsMissingAttendance ?? 0,
          label: 'Webinars missing attendance',
          note: 'Ended over a day ago with no Zoom attendee report — upload it on Webinars',
          phase: false,
          href: '/webinars',
        },
        {
          count: data.attention.withoutIbmAccount,
          label: 'Members without an IBM Cloud account',
          note: 'No directory account exists — provision from Member 360',
          phase: false,
        },
        {
          count: data.attention.ibmLinkPending ?? 0,
          label: 'Members whose IBM account could not be linked',
          note: 'The account exists but its id was unresolvable; the nightly sync usually links it',
          phase: false,
        },
        {
          count: data.attention.inactiveOverYear,
          label: 'Members inactive over 12 months',
          note: 'No event attendance from any connected source',
          phase: false,
        },
        {
          count: data.attention.passwordUndelivered ?? 0,
          label: 'Members who never received their password',
          note: 'Onboarding or a password reset could not send the email — use Send new password',
          phase: false,
          kind: 'password-undelivered' as AttentionKind,
        },
        {
          count: data.attention.ibmDeleted ?? 0,
          label: 'Members whose IBM account was deleted',
          note: 'Confirmed missing in IBM; they cannot sign in anywhere',
          phase: false,
          kind: 'ibm-deleted' as AttentionKind,
        },
        {
          count: data.attention.emailChangeIncomplete ?? 0,
          label: 'Email changes not finished',
          note: 'Some systems still have the old address — retry from Member 360',
          phase: false,
          kind: 'email-change-incomplete' as AttentionKind,
        },
        {
          count: data.attention.moodleUnmatched ?? 0,
          label: 'Moodle accounts with no member',
          note: 'Found by the Moodle sync; no platform member owns them',
          phase: false,
          kind: 'moodle-unmatched' as AttentionKind,
        },
        {
          count: data.attention.formsToClassify ?? 0,
          label: 'New website forms to classify',
          note: 'Forms with entries and no family — accept or ignore them on Website forms',
          phase: false,
          href: '/forms',
        },
        {
          count: data.attention.formResponsesUnmatched ?? 0,
          label: 'Website form responses with no member',
          note: 'The email is not on any member — link them or mark them as guests',
          phase: false,
          href: '/forms',
        },
        {
          count: data.attention.formResponsesMissing ?? 0,
          label: 'Form entries deleted in WordPress',
          note: 'Missing on two pulls in a row — confirm or dismiss; an erasure is not done until confirmed',
          phase: false,
          href: '/forms',
        },
      ].filter((i) => i.count > 0)
    : [];

  return (
    <>
      <PageHeader title="Overview" sub={`Membership and integration health, all sources, ${dateLong(new Date())}.`} />

      {error ? <ErrorNotice>Could not load the dashboard. Retry, or check the API is reachable.</ErrorNotice> : null}

      <KpiBand
        loading={isLoading}
        cells={[
          {
            label: 'Total members',
            value: k ? fmt(k.totalMembers) : '—',
            note: k ? `Across ${k.clusters} clusters` : null,
          },
          {
            label: 'Active members',
            value: k ? fmt(k.activeMembers) : '—',
            note: k ? `${pct(k.activeMembers, k.totalMembers)} of total` : null,
          },
          {
            label: 'New registrations · 30d',
            value: k ? fmt(k.registrations30) : '—',
            note: k ? `${k.registrationsDelta >= 0 ? '+' : ''}${fmt(k.registrationsDelta)} vs prior 30 days` : null,
          },
          {
            label: 'Event attendances · 30d',
            value: k ? fmt(k.attendance30) : '—',
            note: k ? `Across ${fmt(k.sessions30)} sessions` : null,
          },
        ]}
      />

      <div className="grid gap-6 mt-7" style={{ gridTemplateColumns: '1.25fr 1fr' }}>
        <Panel>
          <PanelHeader title="Registrations by month" note="Last 6 months" />
          <PanelBody>
            {isLoading || !data ? (
              <LoadingPanel label="Loading registrations…" />
            ) : (
              <ColumnChart
                data={data.registrationsByMonth.map((m) => ({
                  label: m.label.charAt(0) + m.label.slice(1).toLowerCase(),
                  value: m.count,
                }))}
                currentIndex={data.registrationsByMonth.findIndex((m) => m.current)}
              />
            )}
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHeader title="Integration health" note="5 connections" />
          {isLoading || !data ? (
            <LoadingPanel label="Loading integrations…" height={200} />
          ) : (
            <div>
              <IntegrationRow
                name="IBM Cloud Directory"
                meta={
                  data.integrations.ibmDirectory.at
                    ? `Last sync ${utcStamp(data.integrations.ibmDirectory.at)} UTC · ${fmt(
                        data.integrations.ibmDirectory.records ?? 0,
                      )} users`
                    : 'No sync recorded yet'
                }
                health={data.integrations.ibmDirectory.status}
              />
              <IntegrationRow
                name="Moodle LMS"
                meta={
                  data.integrations.moodle.at
                    ? `Last sync ${utcStamp(data.integrations.moodle.at)} UTC · ${fmt(
                        data.integrations.moodle.records ?? 0,
                      )} records`
                    : 'No sync recorded yet'
                }
                health={data.integrations.moodle.status}
              />
              <IntegrationRow
                name="Zoom attendance"
                meta={
                  data.integrations.attendance.message
                    ? data.integrations.attendance.message.slice(0, 90)
                    : data.integrations.attendance.at
                      ? `Last sync ${utcStamp(data.integrations.attendance.at)} UTC`
                      : 'No sync recorded yet'
                }
                health={data.integrations.attendance.status}
              />
              <IntegrationRow
                name="Gravity Forms webhook"
                meta={
                  data.integrations.webhook.at
                    ? `Last event ${relative(data.integrations.webhook.at)} · Form ${orDash(
                        data.integrations.webhook.formId,
                      )}`
                    : 'No events received yet'
                }
                health={data.integrations.webhook.at ? 'running' : 'planned'}
                pillLabel={data.integrations.webhook.at ? 'Receiving' : 'Planned'}
              />
              <IntegrationRow
                name="Mentorship app"
                meta={
                  data.integrations.mentorship.at
                    ? `Last sync ${utcStamp(data.integrations.mentorship.at)} UTC · ${fmt(
                        data.integrations.mentorship.records ?? 0,
                      )} records`
                    : 'No sync recorded yet'
                }
                health={data.integrations.mentorship.status}
              />
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-6 mt-6" style={{ gridTemplateColumns: '1.25fr 1fr' }}>
        <Panel>
          <PanelHeader title="Latest registrations">
            <button onClick={() => navigate('/members')} className="font-semibold text-small link-underline">
              All members →
            </button>
          </PanelHeader>
          {isLoading || !data ? (
            <SkeletonRows rows={5} widths={['26%', '18%', '20%', '12%']} />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-paper border-b border-line">
                  <th className="font-semibold text-small text-ink-2 text-left px-5 py-[10px]">Member</th>
                  <th className="font-semibold text-small text-ink-2 text-left px-[14px] py-[10px]">Region</th>
                  <th className="font-semibold text-small text-ink-2 text-left px-[14px] py-[10px]">Career stage</th>
                  <th className="font-semibold text-small text-ink-2 text-right px-5 py-[10px]">Received</th>
                </tr>
              </thead>
              <tbody>
                {data.latestRegistrations.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => openMember(r.id)}
                    className="border-b border-line last:border-b-0 hover:bg-selected cursor-pointer"
                  >
                    <td className="px-5 py-3">
                      <div className="text-table font-semibold">
                        {r.firstName} {r.lastName}
                      </div>
                      <div className="text-small text-ink-2">{r.email}</div>
                    </td>
                    <td className="px-[14px] py-3 text-table">{orDash(r.region)}</td>
                    <td className="px-[14px] py-3 text-table">{orDash(r.careerStage)}</td>
                    <td className="px-5 py-3 text-table text-right tabular">{relative(r.receivedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel>
          <PanelHeader title="Needs attention">
            {attentionItems.length > 0 ? (
              <span className="font-semibold rounded-sm bg-warning-tint text-warning text-small px-2 py-[3px] leading-none tabular">
                {attentionItems.length}
              </span>
            ) : null}
          </PanelHeader>
          {isLoading || !data ? (
            <LoadingPanel label="Loading queue…" height={200} />
          ) : attentionItems.length === 0 ? (
            <PanelBody>
              <p className="text-table text-ink-2">
                Nothing needs attention. Every integration is reporting and no member is stale.
              </p>
            </PanelBody>
          ) : (
            <div>
              {attentionItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start justify-between gap-4 px-5 py-[14px] border-b border-line last:border-b-0"
                >
                  <div className="flex gap-3 min-w-0">
                    <span className="font-semibold tabular text-[22px] leading-none shrink-0">{fmt(item.count)}</span>
                    <div className="min-w-0">
                      <div className="text-table">{item.label}</div>
                      <div className="text-small text-ink-2 mt-[2px] flex items-center gap-2">
                        {item.note}
                        {item.phase ? <PhaseChip label="Phase 1" /> : null}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!mutable && !('href' in item)}
                    onClick={() =>
                      'kind' in item && item.kind
                        ? setReviewKind({ kind: item.kind, label: item.label })
                        : navigate('href' in item && item.href ? item.href : '/members')
                    }
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel className="mt-6">
        <PanelHeader title="Onboarding by month" note="IBM accounts set up by the platform" />
        {isLoading || !data ? (
          <LoadingPanel label="Loading onboarding…" height={120} />
        ) : (
          <table className="w-full text-small">
            <thead>
              <tr className="text-left text-ink-2">
                {['Month', 'New account', 'Existing account linked', 'Password not delivered', 'Failed'].map((h) => (
                  <th key={h} className="px-5 py-2 font-normal">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.onboardingByMonth.map((m) => (
                <tr key={m.month} className="border-t border-line tabular">
                  <td className="px-5 py-2">{m.label}</td>
                  <td className="px-5 py-2">{fmt(m.created)}</td>
                  <td className="px-5 py-2">{fmt(m.adopted)}</td>
                  <td className="px-5 py-2">{fmt(m.partial)}</td>
                  <td className="px-5 py-2">{fmt(m.failed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {reviewKind ? (
        <Modal title={reviewKind.label} onClose={() => setReviewKind(null)}>
          {review.isLoading ? <LoadingPanel label="Loading…" height={120} /> : null}
          {review.isError ? <ErrorNotice>Could not load the list.</ErrorNotice> : null}
          <div className="max-h-[50vh] overflow-auto">
            {(review.data ?? []).map((r) =>
              r.moodle ? (
                <div key={r.moodle.moodleUserId} className="py-[10px] border-b border-line last:border-b-0">
                  <div className="text-table font-semibold">
                    {[r.moodle.firstName, r.moodle.lastName].filter(Boolean).join(' ') || '—'}
                  </div>
                  <div className="text-small text-ink-2">
                    {r.moodle.email ?? 'no email'} ·{' '}
                    {r.moodle.auth === 'oauth2'
                      ? 'IBM sign-in, but no IBM account has this email'
                      : (MOODLE_AUTH[r.moodle.auth ?? ''] ?? r.moodle.auth)}{' '}
                    · last access {r.moodle.lastAccessAt ? dateLong(r.moodle.lastAccessAt) : 'never'}
                    {r.moodle.suspended ? ' · suspended' : ''}
                  </div>
                </div>
              ) : r.member ? (
                <button
                  key={`${r.userId}-${r.at}`}
                  type="button"
                  onClick={() => r.member && openMember(r.member.id)}
                  className="w-full text-left py-[10px] border-b border-line last:border-b-0 hover:bg-selected"
                >
                  <div className="text-table font-semibold">
                    {r.member.firstName} {r.member.lastName}
                  </div>
                  <div className="text-small text-ink-2">
                    {r.member.email}
                    {r.at ? ` · ${dateLong(r.at)}` : ''}
                    {r.error ? ` · ${r.error}` : ''}
                  </div>
                </button>
              ) : null,
            )}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
